// Eskom PARCC — capability model (Jun 11 2026 call). PURE: no Firebase, no DB, no
// React. Given a user's resolved access grant, it answers every "may they …?"
// question the UI and the server enforce. Isolated here so the boundary is
// unit-tested exhaustively (see permissions.test.ts).
//
// The Jun 11 call expanded the role set from admin|reviewer to the org structure:
//   • admin           — full control (Lindiwe, + an admin backup).
//   • manager          — cross-division OVERVIEW. Dashboard / Transactions /
//                        Findings / Reports. NEVER edits a working paper. May
//                        allocate ONLY when `canAllocate` is set (Greg yes,
//                        George no).
//   • teamLead         — leads one division. Sees their whole division (plus any
//                        division they are pulled in to assist — the "NTCSA
//                        toggle"), and may REALLOCATE within their own division.
//                        Edits only their own allocations.
//   • reviewer         — division member. Same division-wide VISIBILITY as a team
//                        lead, but no allocation rights. Edits only their own
//                        allocations.
//   • dataCoordinator  — cross-division READ + working-paper download (to populate
//                        the Eskom dashboard). May edit ONLY their own allocations
//                        and ONLY LPO-route transactions.
//
// Division VISIBILITY is read-only: it never widens who may EDIT. Editing always
// follows the allocation (assignedToEmail), so a reviewer seeing their whole
// division can still only capture working papers on transactions handed to them.
//
// ENFORCEMENT NOTE: `canEditTransaction` here gates the UI. ALLOCATION writes are
// server-enforced (admin-api /api/allocate re-derives the caller's grant). The
// transaction/working-paper EDIT mutations, however, are still @auth(level: USER)
// at the connector (a PRE-EXISTING posture from the reviewer-only era), so the
// edit rules below are not yet enforced at the API. Hardening the connector
// (row-level @auth, or routing edits through an admin-api write proxy that calls
// canEditTransaction server-side) is a tracked follow-up.
import { asRole, type Role } from './access';
import type { Transaction } from '../data/types';
import { normalizeEmail } from './email';
import { detectProcurementRoute } from '../domain/classify';
import { divisionKey } from '../domain/divisions';

/**
 * The capability-bearing facts about a signed-in user, resolved from their
 * AccessGrant (or the access verdict when no grant row exists). `email` is the
 * caller's normalised email; `division` is their home division (null for the
 * cross-division roles or an ungranted self-serve user); `canAllocate` only
 * matters for `manager` (it splits Greg from George).
 */
export interface Grant {
  role: Role;
  division: string | null;
  canAllocate: boolean;
  /** A reviewer / team lead granted cross-division VISIBILITY (Lindiwe R4 #3) —
   *  sees every division while keeping their reviewer edit rights. */
  crossDivision?: boolean;
  /** AI Compliance Engine eligibility (TK Jul 2026). Layer 2 of the three-layer
   *  gate — see {@link canUseAi}. Absent/false = no AI (fails CLOSED). */
  aiEnabled?: boolean;
  email: string;
}

/** Human-readable role labels for the admin Users surface. */
export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Administrator',
  manager: 'Manager',
  teamLead: 'Team Lead',
  reviewer: 'Reviewer',
  dataCoordinator: 'Data Coordinator',
};

/** The navigable screens, keyed by their route path (mirrors APP_ROUTES). */
export type RouteKey =
  | 'dashboard'
  | 'aiworkspace'
  | 'transactions'
  | 'working-papers'
  | 'findings'
  | 'allocation'
  | 'manage'
  | 'import'
  | 'bulk-update'
  | 'data-safety'
  | 'reports'
  | 'ai-assurance'
  | 'audit-trail'
  | 'rules'
  | 'users'
  | 'notifications';

/** Compare divisions case-insensitively and whitespace-insensitively. Transaction
 *  `division` is free text (historic casing drift like Gx/GX), so all division
 *  membership goes through this. The population's full labels (e.g. "GX-Head
 *  Office") are reconciled to the roster short form in the DATA (cleanupDivisions.mjs),
 *  not here. */
export function normalizeDivision(value: string | null | undefined): string {
  // Canonical fold (not just trim+lowercase) so a reviewer's grant division and a
  // transaction's division match despite spelling variants — "GX- Head Office" vs
  // "Gx", "Tactical Procurement" vs "Tactical Sourcing", the "ICT and Professional
  // Servises" typo, etc. (Lindiwe R4 feedback #3). See divisionKey.
  return divisionKey(value);
}

/** Roles that see every division's data (no division gate on reads). */
export function canViewAllDivisions(role: Role): boolean {
  return role === 'admin' || role === 'manager' || role === 'dataCoordinator';
}

/**
 * The divisions a user may see at division level. `'all'` for cross-division
 * roles; otherwise JUST their home division. A cross-division allocation does NOT
 * widen this set: the specific allocated transaction stays visible via the
 * own-allocation check in {@link matchesVisible}, but it no longer unlocks the
 * whole foreign division (Lindiwe feedback Jun 2026 — reviewers see only their own
 * division plus the transactions allocated to them; the old "NTCSA assist toggle"
 * that revealed an entire assisted division is removed).
 */
export function visibleDivisions(g: Grant): 'all' | ReadonlySet<string> {
  // The cross-division roles, OR a reviewer/team-lead carrying the crossDivision
  // flag (the coordinators who help everywhere — Lindiwe R4 #3), see every division.
  if (canViewAllDivisions(g.role) || g.crossDivision === true) return 'all';
  const set = new Set<string>();
  const home = normalizeDivision(g.division);
  if (home) set.add(home);
  return set;
}

/** Does this transaction's `assignedToEmail` match the grant's caller (case-insensitive)? */
function isMine(tx: Pick<Transaction, 'assignedToEmail'>, g: Grant): boolean {
  const me = normalizeEmail(g.email);
  if (me === '') return false;
  return normalizeEmail(tx.assignedToEmail) === me;
}

/** Does a transaction fall inside a precomputed visible-division set (or own allocation)? */
function matchesVisible(
  tx: Pick<Transaction, 'division' | 'assignedToEmail'>,
  g: Grant,
  vis: 'all' | ReadonlySet<string>,
): boolean {
  if (vis === 'all') return true;
  if (vis.has(normalizeDivision(tx.division))) return true;
  return isMine(tx, g);
}

/**
 * May this user SEE this transaction? Cross-division roles always can. A
 * division-bound role can see it when it is in their home division, or when it is
 * allocated to them (their own allocation is always visible, even out of their
 * home division). Being allocated a transaction in another division reveals THAT
 * transaction only — never the rest of that division.
 */
export function canViewTransaction(
  tx: Pick<Transaction, 'division' | 'assignedToEmail'>,
  g: Grant,
): boolean {
  return matchesVisible(tx, g, visibleDivisions(g));
}

/**
 * Scope a transaction-like list to what the user may see — the client-side read
 * boundary (the cryptographic one is the server's scoped register). Cross-division
 * roles get everything; division-bound roles get their whole home division plus
 * any transaction allocated to them (no other-division rows, even where they hold
 * a single cross-division allocation). A null grant (auth resolving) sees nothing.
 */
export function scopeTransactions<T extends Pick<Transaction, 'division' | 'assignedToEmail'>>(
  rows: readonly T[],
  g: Grant | null,
): T[] {
  if (!g) return [];
  if (canViewAllDivisions(g.role)) return [...rows];
  const vis = visibleDivisions(g);
  return rows.filter((r) => matchesVisible(r, g, vis));
}

/** Is this an LPO-route transaction? (mechanism / value via the domain classifier,
 *  or the LPO dataset). Used to limit what a data coordinator may edit.
 *
 *  R6 NARROWING (23 Jul 2026, deliberate): the dataset fallback used to match the
 *  merged 'POs / LPOs' bucket, so a data coordinator could edit ordinary purchase
 *  orders too. Now that POs and LPOs are separate datasets, only genuine LPOs
 *  match — the LPO-only rule finally means what it says. Note the value tier in
 *  `detectProcurementRoute` does NOT soften this: the mechanism is tested first,
 *  so a PO carrying any recognised non-LPO mechanism is out of scope at any value.
 *  Pinned in test/lib/permissions.test.ts. */
export function isLpoTransaction(tx: Pick<Transaction, 'mechanism' | 'value' | 'dataset'>): boolean {
  if (detectProcurementRoute({ mechanism: tx.mechanism, value: tx.value }) === 'lpo') return true;
  return /lpo/i.test(String(tx.dataset ?? ''));
}

/**
 * May this user EDIT (capture/review) this transaction? Always allocation-bound
 * for non-admins — division visibility never grants edit. Admin: any. Manager:
 * never (overview only). Reviewer / team lead: their own allocation. Data
 * coordinator: their own allocation AND only when it is an LPO.
 */
export function canEditTransaction(
  tx: Pick<Transaction, 'assignedToEmail' | 'mechanism' | 'value' | 'dataset'>,
  g: Grant | null,
): boolean {
  if (!g) return false;
  if (g.role === 'admin') return true;
  if (g.role === 'manager') return false;
  if (!isMine(tx, g)) return false;
  if (g.role === 'dataCoordinator') return isLpoTransaction(tx);
  return true; // reviewer / teamLead own allocation
}

/** May this user allocate transactions at all? (Scope is enforced per-division by
 *  {@link canAllocateInDivision}.) */
export function canAllocate(g: Grant | null): boolean {
  if (!g) return false;
  if (g.role === 'admin') return true;
  if (g.role === 'manager') return g.canAllocate === true;
  if (g.role === 'teamLead') return true;
  return false;
}

/** May this user allocate a transaction in the given division? Team leads are
 *  confined to their own division; admins and allocating managers are not. */
export function canAllocateInDivision(g: Grant | null, division: string | null | undefined): boolean {
  if (!g) return false;
  if (g.role === 'admin') return true;
  if (g.role === 'manager') return g.canAllocate === true;
  if (g.role === 'teamLead') return normalizeDivision(division) === normalizeDivision(g.division) && normalizeDivision(g.division) !== '';
  return false;
}

/**
 * May this caller author (create / edit / issue) a Report to Management for
 * `division`? admin + manager: any division. teamLead: ONLY their own. Everyone
 * else (reviewer, dataCoordinator, null): no. This is the UI gate — the SERVER
 * (admin-api rtm.js canAuthorRtm) is the boundary and re-checks every write.
 */
export function canAuthorRtm(g: Grant | null, division: string | null | undefined): boolean {
  if (!g) return false;
  if (g.role === 'admin' || g.role === 'manager') return true;
  if (g.role === 'teamLead') {
    const d = normalizeDivision(division);
    return d !== '' && d === normalizeDivision(g.division);
  }
  return false;
}

/** May this role download working papers? Every recognised role may. Managers
 *  were previously excluded (overview-only), but Lindiwe's R4 feedback (Jul 2026)
 *  asked for managers to have download access; since they already view the papers
 *  read-only and download the aggregate reports, a single working-paper download
 *  is a consistent extension of their oversight remit. */
export function canDownloadWorkingPaper(role: Role): boolean {
  return (
    role === 'admin' ||
    role === 'manager' ||
    role === 'teamLead' ||
    role === 'reviewer' ||
    role === 'dataCoordinator'
  );
}

/** May this role BULK-download working papers (the "All reviewed" / "Compliance"
 *  cross-population XLSX exports)? The cross-division oversight roles: admin,
 *  dataCoordinator, and — since Lindiwe's R4 feedback — manager. Team leads and
 *  reviewers keep single-paper downloads only (scoped to what they see). */
export function canBulkDownloadWorkingPapers(role: Role): boolean {
  return role === 'admin' || role === 'dataCoordinator' || role === 'manager';
}

/** May this grant open the CES workbook UPLOAD screen (R5 #1)? The capture
 *  roles: admin, teamLead, reviewer, dataCoordinator. Managers are overview-only
 *  (canEditTransaction refuses them per row anyway — hiding the screen just
 *  avoids a dead end). Real enforcement stays PER TRANSACTION via
 *  {@link canEditTransaction} inside the upload matcher, exactly like manual
 *  capture: a reviewer's upload imports only their own allocations. */
export function canUploadCesWorkbook(g: Grant | null): boolean {
  if (!g) return false;
  return g.role === 'admin' || g.role === 'teamLead' || g.role === 'reviewer' || g.role === 'dataCoordinator';
}

/** May this grant open the BULK UPDATE screen (Lindiwe R6)? Administrators only.
 *  Unlike the CES workbook upload, this screen writes the FULL manual-editor
 *  surface — compliance level, review status, findings — across a whole file in
 *  one confirm, so the screen itself is the narrow gate. Per-row
 *  {@link canEditTransaction} still applies inside the matcher, so widening this
 *  later (to teamLead, say) would not hand anyone rows they cannot already edit. */
export function canBulkUpdateTransactions(g: Grant | null): boolean {
  return g != null && g.role === 'admin';
}

/** May this role VIEW the working papers (read-only)? Everyone with a recognised
 *  role may — including managers, who gain read-only access so they can leave QA
 *  notes (Lindiwe feedback Jun 23 2026, 4a). Editing is gated separately by
 *  {@link canEditTransaction} (managers stay read-only). */
export function canViewWorkingPaper(role: Role): boolean {
  return (
    role === 'admin' ||
    role === 'manager' ||
    role === 'teamLead' ||
    role === 'reviewer' ||
    role === 'dataCoordinator'
  );
}

/** May this role add / resolve QA notes on a working paper? Managers + admins only
 *  (the QA reviewers — Greg/George — and administrators). Feedback 4a. */
export function canQaWorkingPaper(role: Role): boolean {
  return role === 'admin' || role === 'manager';
}

/** May this grant download a divisional report for `targetDivision`? Admin +
 *  manager (cross-division oversight) may access any division; a team lead may
 *  access ONLY their own division. Feedback 2c. */
export function canDownloadDivisionalReport(g: Grant | null, targetDivision: string | null | undefined): boolean {
  if (!g) return false;
  if (g.role === 'admin' || g.role === 'manager') return true;
  if (g.role === 'teamLead') return normalizeDivision(targetDivision) === normalizeDivision(g.division) && normalizeDivision(g.division) !== '';
  return false;
}

/**
 * May this grant use the AI Compliance Engine? Layer 2 of the three-layer gate:
 *
 *   1. global kill switch  — env on admin-api; stops everything at once
 *   2. THIS — `AccessGrant.aiEnabled`, admin-granted per person
 *   3. `uiPrefs.aiMode`    — the person's own Settings switch
 *
 * Layers 1+2 are re-derived server-side on every `/api/ai/*` call; layer 3 is a
 * localStorage preference and therefore decides only what a person SEES. Forcing
 * `aiMode: 'on'` in devtools reveals nothing and authorises nothing.
 *
 * Fails CLOSED: a null grant (auth still resolving, or no grant row) and an
 * absent flag both return false. Note this is grant-gated, NOT admin-gated —
 * Aziz and Lindiwe are the first users and neither is necessarily an admin.
 *
 * Deliberately NOT carried by {@link impersonationGrant}: an admin previewing as
 * someone else gets their view, never their ability to spend on inference.
 */
export function canUseAi(g: Grant | null): boolean {
  return g != null && g.aiEnabled === true;
}

/** May this grant reach the given screen? Drives both Nav and the router guard. A
 *  null grant (auth resolving / unknown) is admitted only to the shared screens. */
export function canAccessRoute(routeKey: RouteKey, g: Grant | null): boolean {
  switch (routeKey) {
    case 'dashboard':
    case 'transactions':
      return true; // every authenticated session, including a still-resolving one
    case 'aiworkspace':
      // Per-person rollout: invisible until an admin grants aiEnabled AND the
      // person turns it on in Settings. Nobody is switched on by a deploy.
      return canUseAi(g);
    case 'working-papers':
      // Managers gain read-only access (4a) so they can leave QA notes; everyone
      // else who works the papers already had it.
      return g == null || canViewWorkingPaper(g.role);
    case 'findings':
      return g != null && (g.role === 'admin' || g.role === 'manager');
    case 'reports':
      // Team leads reach Reports for their own division's archive (2c).
      return g != null && (g.role === 'admin' || g.role === 'manager' || g.role === 'teamLead');
    case 'ai-assurance':
      // Crosses divisions (report-lead roles) AND is AI data (aiEnabled) — the
      // same gate the /api/ai/review/latest-all endpoint enforces server-side.
      return canUseAi(g) && g != null && (g.role === 'admin' || g.role === 'manager' || g.role === 'teamLead');
    case 'audit-trail':
      // Everyone with a grant: the feed is scoped SERVER-SIDE (cross-division roles
      // see the whole window; a division-bound grant only its own transactions'
      // history + rows it authored), so this honours the same boundary rather than
      // being a second, looser one.
      return g != null;
    case 'allocation':
      return canAllocate(g);
    case 'manage':
    case 'import':
    case 'bulk-update':
    case 'data-safety':
    case 'rules':
    case 'users':
    case 'notifications':
      return g != null && g.role === 'admin';
    default:
      return false;
  }
}

// --- Impersonation (admin "view as" preview) -------------------------------
// An admin may preview the app as any NON-admin role to verify the role-based
// views. Impersonating another admin is pointless (same view), so it is excluded.

/** The roles an admin may preview as. */
export const IMPERSONATABLE_ROLES: readonly Role[] = ['manager', 'teamLead', 'reviewer', 'dataCoordinator'];

/** The minimal facts an impersonation grant is built from (an AccessGrant record). */
export interface ImpersonationSource {
  email: string;
  role: string;
  division?: string | null;
  canAllocate?: boolean | null;
}

/**
 * Build the effective {@link Grant} an admin previews as, from a target person's
 * access-grant record. Returns null for an admin or unrecognised role — there is
 * nothing meaningful to preview. The email is normalised so own-allocation checks
 * (canEditTransaction, scoping) resolve against the target.
 */
export function impersonationGrant(source: ImpersonationSource): Grant | null {
  const role = asRole(source.role);
  if (!role || role === 'admin') return null;
  return {
    role,
    division: source.division ?? null,
    canAllocate: source.canAllocate === true,
    email: normalizeEmail(source.email),
  };
}
